import type {
  InterstateOnChangeEvent,
  SearchInputChangeEventValue,
  SelectInputEventValue
} from "@dealer-center/core-components";
import {
  Alert,
  FilterInput,
  Grid,
  Select
} from "@dealer-center/core-components";
import type { CompareToolContentProps, filterType } from "./interface";
import { useEffect, useState } from "react";
import { CompareToolContentItems } from "./useCompareToolContenItems";
import { filterItems, getPairedFilterItemsByLabel } from "./helper";
import styled from "styled-components";
import { LoadingIndicator } from "../LoadingIndicator";

const StyledCompareToolContent = styled.div`
  padding: 1rem;
`;
const StyledHeader = styled.h2`
  text-transform: lowercase;
  margin: 0;
  word-wrap: break-word;
`;
const filterOptions: { label: string; value: filterType }[] = [
  {
    label: "All",
    value: "All"
  },
  {
    label: "Similar",
    value: "similar"
  },
  {
    label: "Different",
    value: "different"
  }
];
export const CompareToolContent = ({
  compareFromComponent,
  compareToComponent,
  itemsToCompare
}: CompareToolContentProps) => {
  const [filter, setFilter] = useState<filterType>("All");
  const [searchText, setSearchText] = useState<string>("");
  const [pairedFilterItemsByLabel, setPairedFilterItemsByLabel] = useState<
    ReturnType<typeof getPairedFilterItemsByLabel>
  >([]);

  useEffect(() => {
    setPairedFilterItemsByLabel(getPairedFilterItemsByLabel(itemsToCompare));
  }, [itemsToCompare]);

  return (
    <StyledCompareToolContent>
      <Grid container spacing={2}>
        <Grid size={{ sm: 6 }}>{compareFromComponent}</Grid>
        <Grid size={{ sm: 6 }}>{compareToComponent}</Grid>
      </Grid>
      <Grid container paddingBottom={2} paddingTop={2} spacing={2}>
        <Grid size={{ sm: 12 }}>
          <FilterInput
            id="core-custom-components-compare-tool-filterbytext"
            label="Filter by text"
            name="core-custom-components-compare-tool-filterbytext"
            placeholder="Filter"
            size="medium"
            value={searchText}
            onChange={(
              e: InterstateOnChangeEvent<SearchInputChangeEventValue>
            ) => {
              setSearchText(e.target.value as string);
            }}
          />
        </Grid>
        <Grid size={{ sm: 12 }}>
          <Select
            displayDeselectOption={false}
            id="core-custom-components-compare-tool-filterbydifference"
            label="Filter by difference"
            name="core-custom-components-compare-tool-filterbydifference"
            options={filterOptions}
            placeholder="Select"
            onChange={(e: InterstateOnChangeEvent<SelectInputEventValue>) =>
              setFilter(e.target.value as filterType)
            }
          />
        </Grid>
      </Grid>
      {(itemsToCompare.from?.label || itemsToCompare.to?.label) && (
        <Grid container spacing={2}>
          <Grid size={{ sm: 6 }}>
            <StyledHeader>{itemsToCompare?.from?.label}</StyledHeader>
          </Grid>
          <Grid size={{ sm: 6 }}>
            <StyledHeader>{itemsToCompare?.to?.label}</StyledHeader>
          </Grid>
        </Grid>
      )}
      {(itemsToCompare.from?.value.isLoading ||
        itemsToCompare.to?.value.isLoading) && (
        <Grid container spacing={2}>
          <Grid size={{ sm: 6 }}>
            {itemsToCompare.from?.value.isLoading && (
              <LoadingIndicator actionName="Loading account compare from" />
            )}
          </Grid>
          <Grid size={{ sm: 6 }}>
            {itemsToCompare.to?.value.isLoading && (
              <LoadingIndicator actionName="Loading account compare to" />
            )}
          </Grid>
        </Grid>
      )}
      {(itemsToCompare.from?.value.isError ||
        itemsToCompare.to?.value.isError) && (
        <Grid container spacing={2}>
          <Grid size={{ sm: 6 }}>
            {itemsToCompare.from?.value.isError && (
              <Alert type={"error"}>
                {itemsToCompare.from?.value?.error?.message}
              </Alert>
            )}
          </Grid>
          <Grid size={{ sm: 6 }}>
            {itemsToCompare.to?.value.isError && (
              <Alert type={"error"}>
                {itemsToCompare.to?.value?.error?.message}
              </Alert>
            )}
          </Grid>
        </Grid>
      )}
      {pairedFilterItemsByLabel?.map(item => {
        if (filterItems(filter, searchText, item)) {
          return (
            <CompareToolContentItems
              from={item.from}
              key={item.label}
              label={item.label}
              to={item.to}
            />
          );
        }
        return null;
      })}
    </StyledCompareToolContent>
  );
};
